import { create } from 'zustand';
import { cleanUrl, hashEmail } from '../utils';
import { authAPI } from '../apis/auth';
import { createJSONStorage, persist } from 'zustand/middleware';

type MfaResult<T = Array<null>> = ({ success: true } & T) | { success: false; errorMessage: string; error: unknown };

type State = {
  sessions: Record<string, number>;
};

type Action = {
  hasMfaSession: (params: { email: string }) => MfaResult<{ hasSession: boolean }>;
  checkMfaBioRequired: (params: { email: string }) => Promise<MfaResult<{ isBioOnly: boolean }>>;
  executeMfaLogin: (params: { email: string; uid: string }) => Promise<MfaResult<{ jwtTokenResponse: string }>>;
  isSkipMfaLogin: (params: { currentChannel: string }) => Promise<MfaResult<{ skip: boolean }>>;
  validateJwtTokenResponse: (params: {
    jwtTokenResponse: string;
    isBioOnly: boolean;
  }) => Promise<MfaResult<{ isValid: boolean }>>;
  setMfaSession: (params: { email: string }) => void;
};

type MfaLoginStore = State & Action;

export const useMfaLoginStore = create<MfaLoginStore>()(
  persist(
    (set, get) => ({
      sessions: {},

      setMfaSession: ({ email }: { email: string }) => {
        const hashedKey = hashEmail(email);
        const expirationTime = Date.now() + 60 * 60 * 1000;

        set((state) => ({
          sessions: {
            ...state.sessions,
            [hashedKey]: expirationTime,
          },
        }));
      },

      hasMfaSession: ({ email }) => {
        try {
          const sessions = get().sessions;
          const hashedKey = hashEmail(email);
          const expirationTime = sessions[hashedKey];

          if (!expirationTime) {
            return { success: true, hasSession: false };
          }

          if (Date.now() > expirationTime) {
            // 만료된 세션 제거
            set((state) => {
              const newSessions = { ...state.sessions };
              delete newSessions[hashedKey];
              return { sessions: newSessions };
            });
            return { success: true, hasSession: false };
          }

          return { success: true, hasSession: true };
        } catch (error) {
          return {
            success: false,
            errorMessage: 'Failed to check MFA session',
            error,
          };
        }
      },

      checkMfaBioRequired: async ({ email }) => {
        try {
          const [bioRequiredChannels, userChannelsData, mfaExceptionData] = await Promise.all([
            authAPI.getBioRequiredChannels(),
            authAPI.getUserChannels({ email }),
            authAPI.getMfaException({ email }),
          ]);

          const { channelList } = userChannelsData;
          const { isMfaException } = mfaExceptionData;

          const hasBioRequiredChannel = channelList.some((channel: string) => bioRequiredChannels.includes(channel));

          return {
            success: true,
            isBioOnly: hasBioRequiredChannel && !isMfaException,
          };
        } catch (error) {
          return {
            success: false,
            errorMessage: 'Failed to check MFA Bio Required',
            error,
          };
        }
      },

      executeMfaLogin: async ({ email, uid }) => {
        try {
          const bioCheckResult = await get().checkMfaBioRequired({ email });

          if (!bioCheckResult.success) {
            return bioCheckResult;
          }

          const jwtTokenResponse = await authAPI.initiateMfaLogin({
            email,
            uid,
            isBioOnly: bioCheckResult.isBioOnly,
            returnUrl: cleanUrl(['gig_events']) + `&isBioOnly=${bioCheckResult.isBioOnly}`,
          });

          return {
            success: true,
            jwtTokenResponse,
          };
        } catch (error) {
          return {
            success: false,
            errorMessage: 'Failed to MFA Login',
            error,
          };
        }
      },

      isSkipMfaLogin: async ({ currentChannel }) => {
        try {
          const mfaRequiredChannels = await authAPI.getMfaRequired();
          mfaRequiredChannels.push('btp');

          const enteredSkipChannel = !mfaRequiredChannels.includes(currentChannel);

          if (enteredSkipChannel)
            return {
              success: true,
              skip: true,
            };

          return {
            success: true,
            skip: false,
          };
        } catch (error) {
          return {
            success: false,
            errorMessage: 'Failed to check if MFA login is skipped',
            error,
          };
        }
      },

      validateJwtTokenResponse: async ({ jwtTokenResponse, isBioOnly }) => {
        try {
          const isValid = await authAPI.validateJwtToken({ jwtTokenResponse, isBioOnly });

          if (isValid === 'fail') {
            return {
              success: true,
              isValid: false,
            };
          }

          return { success: true, isValid: true };
        } catch (error) {
          return {
            success: false,
            errorMessage: 'Failed to validate JWT token response',
            error,
          };
        }
      },
    }),
    {
      name: 'mfa-store', // localStorage에 저장될 키 이름
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
